<template>
  <div>
    <div class="flex flex-col gap-y-4 mb-24">
      <div class="bg-accent py-8 px-52">
        <VideoPlayer :options="state.videoOptions" />
      </div>
      <div class="flex justify-between px-52 gap-x-4 text-foreground mb-24">
        <div class="flex flex-col">
          <h3 class="text-2xl mb-4 font-semibold">
            {{ recordingTitle }}
          </h3>
          <p class="text-sm">{{ state.selectedCompanyRecording?.description }}</p>
        </div>
        <div class="flex gap-2 py-2">
          <Button
            v-if="state.selectedCompanyRecording?.show_admin_panel"
            :as="NuxtLink"
            :to="`/admin/recordings/${state.selectedCompanyRecording?.id}`"
            variant="outline"
          >
            <PencilIcon class="w-4 h-4" />
            Edit
          </Button>
          <Button
            v-if="state.selectedCompanyRecording?.download_url"
            variant="outline"
            as="a"
            :href="state.selectedCompanyRecording?.download_url"
            download
            target="_blank"
            data-testid="download-recording-btn"
            @click="trackDownload"
          >
            <DownloadIcon class="w-4 h-4" />
            Download
          </Button>
          <Button @click="shareModalState.isOpen = true">
            <Share2Icon class="w-4 h-4" />
            Share
          </Button>
        </div>
      </div>
    </div>

    <AdminDialogsShare
      :is-open="shareModalState.isOpen"
      :url="shareModalState.url"
      :title="shareModalState.title"
      :entity="shareModalState.entity"
      resource-type="Recording"
      @update:is-open="shareModalState.isOpen = $event"
      @embed="goToEmbed"
    />
  </div>
</template>

<script setup lang="ts">
import type { BreadcrumbData } from '@/components/ui/breadcrumb/BreadcrumbTrail.vue'
import type { PlayerOptions } from '~~/entities/video-player'
import { PencilIcon, DownloadIcon, Share2Icon } from 'lucide-vue-next'
import type { ShareModalState } from '@/components/admin/dialogs/AdminDialogsShare.vue'
import type { CompanyRecording, CompanyRecordingAPIQueryParams } from '~~/entities/recording'
import { useToast } from '~/components/ui/toast'
import type { Company } from '~~/entities/company'

const { toast } = useToast()

const state = reactive({
  selectedCompanyRecording: {} as CompanyRecording,
  pageTitle: '',
  recordingDescription: '',
  company: null as Company | null,
  videoOptions: {
    src: '',
    type: 'video/mp4',
    poster: '',
  } as PlayerOptions | undefined,
})
const shareModalState = ref<ShareModalState>({
  isOpen: false,
  url: '',
  title: '',
  entity: undefined,
})

const NuxtLink = resolveComponent('NuxtLink')
const { $analytics } = useNuxtApp()

const config = useRuntimeConfig()
const router = useRouter()
const route = useRoute()

const { selectedCompany, getCompany } = useCompanies()
const { state: userState } = useUser()

async function getRecordingByUUID(uuid: string, params?: CompanyRecordingAPIQueryParams) {
  return $fetch<CompanyRecording>(`/v2/meetings/recordings/${uuid}`, {
    ...useFetchSharedOptions(),
    params,
  })
}

const recordingTitle = computed(() => {
  if (state.company?.name) {
    return `${state.selectedCompanyRecording?.name} - ${state.company?.name}`
  }

  return state.selectedCompanyRecording?.name
})

const uuid = computed(() => (Array.isArray(route.params?.uuid) ? route.params?.uuid[0] : route.params?.uuid))

try {
  state.selectedCompanyRecording = await getRecordingByUUID(uuid.value, {
    include: ['thumbnail'],
  })
} catch {
  toast({
    title: 'Recording Not Found',
    description: 'The recording you are trying to view does not exist.',
    variant: 'destructive',
  })

  setTimeout(() => {
    navigateTo('/admin/recordings')
  }, 0)
}

onMounted(async () => {
  $analytics.track('VideoRecordingLandingPageView')

  if (state.selectedCompanyRecording) {
    state.videoOptions = {
      src: state.selectedCompanyRecording.download_url,
      type: 'video/mp4',
      poster: state.selectedCompanyRecording.thumbnail ?? '',
    }

    shareModalState.value.url = `${config.public.WEB_ENDPOINT}meetings/recordings/${state.selectedCompanyRecording.uuid}`
    shareModalState.value.title = state.selectedCompanyRecording.name ?? selectedCompany.value?.name
    shareModalState.value.entity = userState.value.user?.id ? state.selectedCompanyRecording : undefined

    state.company = await getCompany(state.selectedCompanyRecording.company_id)
  }
})

function trackDownload() {
  $analytics.track('VideoRecordingLandingPageClickDownload')
}

async function goToEmbed(recording: CompanyRecording) {
  if (userState.value.user?.id) {
    const { listCompanyEmbedVideos } = useCompanyEmbedVideos(selectedCompany.value?.id)

    const { count, results } = await listCompanyEmbedVideos({
      meeting_record_id: recording.id,
    })

    if (count > 1) {
      await router.push('/admin/embeds?searchTerm=' + recording.name)
    } else if (count === 1) {
      await router.push(`/admin/embeds/video/${results[0].id}`)
    } else {
      await router.push(`/admin/embeds/video?recording_id=${recording.id}`)
    }
  }
}

const pageTitle = computed(() => {
  if (state.selectedCompanyRecording?.name) {
    return state.selectedCompanyRecording.name
  }

  if (selectedCompany.value?.name) {
    return selectedCompany.value.name
  }

  return undefined
})

useSeoMeta({
  title: () => pageTitle.value,
  description: () => state.selectedCompanyRecording?.description ?? '',
  ogDescription: () => state.selectedCompanyRecording?.description ?? '',
  ogImage: () => state.company?.logo ?? '',
  ogVideo: () => state.selectedCompanyRecording?.download_url ?? '',
})
</script>
